import {
  Box,
  Checkbox,
  Group,
  Paper,
  ScrollArea,
  SimpleGrid,
  Stack,
  Text,
  Title
} from '@mantine/core';
import { useDocumentTitle } from '@mantine/hooks';
import { useRoomPrices } from '@pages/Client/Calendar/hooks/useRoomPrices';
import useMinstay from '@pages/Client/PricingStrategy/MinStay/hooks/useMinstay';
import { useTranslation } from 'react-i18next';
import { useHotelDetails } from '@pages/Client/hooks/useHotelDetails';
import { Controller, useForm } from 'react-hook-form';
import { MinstaySchema } from '@pages/Client/Calendar/utils/validateBaseSettings';
import { Icon } from '@common/components/foundations/icons';
import { zodResolver } from '@hookform/resolvers/zod';
// Keep for later when introducing autosave
// import { debounce } from 'lodash-es';
import { useEffect } from 'react';
import { useUpdatePricingSettings } from '@pages/Client/hooks/useUpdatePricingSettings';
import { useFormSubmitStore } from '@pages/Client/PricingStrategy/MinStay/store/formSubmitStore';
import { MinStay } from '@pages/Client/PricingStrategy/MinStay/types';
import { useEventTracking } from '@common/hooks/useEventTracking';
import { useMinStaySettings, useSaveMinStaySettings } from '@pages/Client/hooks/useMinStaySettings';
import { useAppFlags } from '@common/hooks/useAppFlags';
import { useHandleMinStaySettings } from '@pages/Client/Calendar/hooks/useHandleMinStaySettings';
import { extractMinStayDates } from '@pages/Client/Calendar/utils/extractMinStayDates';

export const MinStayOrphanGapPage = () => {
  const { t } = useTranslation();
  useDocumentTitle(t('Orphan Gap Correction'));

  const { isMinStayV3Enabled } = useAppFlags();

  const { minStaySettings } = useMinStaySettings();
  const { pricingSettings, pricingSettingsQuery } = useRoomPrices();
  const { hotelDetails } = useHotelDetails();
  const { savePricingSettings, isLoading } = useUpdatePricingSettings();
  const { trackEvent } = useEventTracking();
  const { saveMinStaySettings } = useSaveMinStaySettings();
  const { prepareMinStaySettings } = useHandleMinStaySettings();

  const settings = isMinStayV3Enabled ? minStaySettings : pricingSettings;

  const { control, handleSubmit, setValue } = useForm<MinStay>({
    resolver: zodResolver(MinstaySchema)
  });

  const { roomTypesOption } = useMinstay();
  const setSubmitHandler = useFormSubmitStore((state) => state.setSubmitHandler);
  const setIsSaving = useFormSubmitStore((state) => state.setIsSaving);

  const ROOM_TYPES_OPTION = roomTypesOption(pricingSettings?.rooms) || [];

  useEffect(() => {
    if (!settings?.minstay?.enabled_orphan_gap_correction_room_ids) return;
    setValue(
      'enabled_orphan_gap_correction_room_ids',
      settings?.minstay?.enabled_orphan_gap_correction_room_ids
    );
  }, [settings]);

  const saveOrphanGap = async (data: MinStay) => {
    // Always get the latest data first to avoid race conditions
    const { data: latestPricingSettings } = await pricingSettingsQuery.refetch();

    // Ensure we have the latest data
    if (!latestPricingSettings) {
      throw new Error('Failed to fetch latest settings');
    }

    // Update pricing settings - preserve all existing data
    const newPricingSettings = {
      ...latestPricingSettings,
      minstay: {
        ...latestPricingSettings?.minstay,
        enabled_orphan_gap_correction_room_ids: data.enabled_orphan_gap_correction_room_ids || []
      }
    };

    const minStayDates = extractMinStayDates(newPricingSettings.dates);
    const { minStaySettingsForAPI } = await prepareMinStaySettings(minStayDates);

    const newMinStaySettings = {
      ...minStaySettingsForAPI,
      minstay: {
        ...minStaySettingsForAPI?.minstay,
        enabled_orphan_gap_correction_room_ids: data.enabled_orphan_gap_correction_room_ids || []
      }
    };

    // Save pricing settings first
    await savePricingSettings(JSON.stringify(newPricingSettings));
    await saveMinStaySettings(JSON.stringify(newMinStaySettings));

    trackEvent('OrphanGapCorrectionApplied', {
      tags: {
        all_rooms: data.enabled_orphan_gap_correction_room_ids?.length === ROOM_TYPES_OPTION.length
      }
    });
  };

  // Keep for later when introducing autosave
  // const debouncedSaveOrphanGap = debounce(handleSubmit(saveOrphanGap), 500);

  useEffect(() => {
    if (isLoading) {
      setIsSaving(true);
    } else {
      setIsSaving(false);
    }
  }, [isLoading]);

  useEffect(() => {
    setSubmitHandler(handleSubmit(saveOrphanGap));
  }, [handleSubmit, saveOrphanGap, setSubmitHandler]);

  return (
    <Box>
      <SimpleGrid
        cols={{
          sm: 1,
          md: 2
        }}
      >
        <Stack
          maw={{
            base: '100%',
            md: '80%'
          }}
        >
          <Title order={3} fw={400}>
            {t('Orphan Gap Correction')}
          </Title>

          <Text c="gray.9">
            {t(
              `Orphan Gap correction lets you automatically correct impossible minimum stay restrictions so that you never have available {{apartmentOrRoom}}s that cannot be booked.`,
              {
                apartmentOrRoom: hotelDetails?.room_apartment_space_name.toLowerCase()
              }
            )}
          </Text>

          <Text c="gray.9">
            {t(
              `If we see that there are less days available for a {{apartmentOrRoom}} type than are needed to meet the minimum stay restriction in place, then we will automatically reduce the length of the minimum stay restriction to fit the number of consecutive dates available.`,
              {
                apartmentOrRoom: hotelDetails?.room_apartment_space_name.toLowerCase()
              }
            )}
          </Text>
          <Text c="gray.9">
            {t(
              'For example, if you have a minimum stay of 4 nights on a Thursday, but both the Tuesday before and the Saturday after are fully booked then we will reduce the length of the minimum stay restriction to 3 nights.'
            )}
          </Text>
        </Stack>

        <Box>
          <Paper w="100%" p="md">
            <Stack>
              <Controller
                name="enabled_orphan_gap_correction_room_ids"
                control={control}
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <Checkbox.Group
                    value={value}
                    error={error?.message}
                    onChange={(value) => {
                      onChange(value);
                      // Keep for later when introducing autosave
                      // debouncedSaveOrphanGap();
                    }}
                    label={`${t(`{{apartmentOrRoom}} Types`, {
                      apartmentOrRoom: hotelDetails?.room_apartment_space_name
                    })}`}
                    description={`Choose all ${t(
                      `{{apartmentOrRoom}} types that you want to enable Orphan Gap correction for`,
                      {
                        apartmentOrRoom: hotelDetails?.room_apartment_space_name.toLowerCase()
                      }
                    )}`}
                  >
                    <ScrollArea.Autosize type="always" mah="800px" offsetScrollbars scrollbars="y">
                      <Stack pt="xs" gap="xs">
                        {ROOM_TYPES_OPTION.map((roomType) => (
                          <Checkbox.Card
                            px="md"
                            py="xs"
                            radius="lg"
                            value={roomType.value}
                            key={roomType.value}
                            className="group bg-white hover:bg-[var(--mantine-color-default-hover)] disabled:cursor-not-allowed disabled:opacity-50 data-[checked]:border-[var(--mantine-primary-color-filled)]"
                          >
                            <Group wrap="nowrap" align="center" justify="space-between">
                              <Box>
                                <Text c="dark" size="sm">
                                  {roomType.label}
                                </Text>
                              </Box>
                              <div className="hidden shrink-0 text-indigo group-data-[checked]:flex">
                                <Icon.CheckList className="h-5 w-5" />
                              </div>
                            </Group>
                          </Checkbox.Card>
                        ))}
                      </Stack>
                    </ScrollArea.Autosize>
                  </Checkbox.Group>
                )}
              />
            </Stack>
          </Paper>
        </Box>
      </SimpleGrid>
    </Box>
  );
};

export default MinStayOrphanGapPage;
